<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import type { SnackbarMessagePayload } from '@/store/models/snackbar'

interface IProps {
  modelValue?: boolean,
  message?: SnackbarMessagePayload,
  contentProps?: Record<string, unknown>,
  zIndex?: number,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  message: undefined,
  contentProps: undefined,
  zIndex: undefined,
})

interface Emit {
  (e: 'update:modelValue', value: boolean): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

const snackbar = computed(() => {
  if (!props.message) return snackbarStore

  return {
    ...props.message,
    timeout: props.message.timeout ?? (props.message.color === 'error' ? 600000 : 5000),
    lines: props.message.lines || [],
    location: props.message.location || 'top',
    actionLabel: props.message.actionLabel || '',
    onAction: props.message.onAction || null,
  }
})

const closeMessage = () => {
  if (props.message) {
    emit('update:modelValue', false)
    return
  }

  snackbarStore.closeMessage()
}
</script>

<template>
  <VSnackbar
    :model-value="modelValue"
    :color="snackbar.color"
    :timeout="snackbar.timeout"
    :location="snackbar.location"
    :content-props="contentProps"
    :z-index="zIndex"
    auto-height
    @update:modelValue="value => emit('update:modelValue', value)"
  >
    <div class="global-snackbar-content d-flex align-start">
      <VIcon
        size="30"
        :icon="snackbar.icon"
      />
      <div class="global-snackbar-message d-flex flex-column padding-left-5">
        <div>
          <strong>{{ snackbar.title }}</strong>
        </div>
        <div v-if="snackbar.text">
          {{ snackbar.text }}
        </div>
        <ul
          v-if="snackbar.lines.length"
          class="global-snackbar-list mt-2 mb-0 pl-5"
        >
          <li
            v-for="(line, index) in snackbar.lines"
            :key="`${index}-${line}`"
            class="mb-1"
          >
            {{ line }}
          </li>
        </ul>
      </div>
      <VBtn
        v-if="snackbar.actionLabel"
        class="ml-3"
        color="light"
        variant="text"
        @click.stop="snackbar.onAction?.(); closeMessage()"
      >
        {{ snackbar.actionLabel }}
      </VBtn>
    </div>
  </VSnackbar>
</template>

<style scoped>
.global-snackbar-content {
  max-inline-size: min(900px, 85vw);
}

.global-snackbar-message {
  min-inline-size: 0;
}

.global-snackbar-list {
  max-block-size: min(420px, 50vh);
  overflow-y: auto;
  overflow-wrap: anywhere;
  padding-inline-end: 12px;
}
</style>
